"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import {
  ChevronLeft,
  ChevronRight,
  MessageCircleQuestion,
  Search,
} from "lucide-react";
import { ZoblissLogo } from "@/components/icons/common";
import { Footer } from "@/components/landing/footer";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import InlineLoader from "@/components/ui/InlineLoader";
import { Input } from "@/components/ui/input";
import useApi from "@/hooks/useApi";
import { useDebounce } from "@/hooks/useDebounce";
import ProductUpdateDescription from "@/components/product-updates/ProductUpdateDescription";
import {
  buildProductUpdateTagList,
  formatProductUpdateDate,
  getProductUpdateTagLabel,
} from "@/lib/product-updates";
import { cn } from "@/lib/utils";

const IMAGE_ATTACHMENT_REGEX = /\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i;

function isImageAttachment(update) {
  const mimeType = String(update?.media_mime_type || "").toLowerCase();
  if (mimeType.startsWith("image/")) return true;

  const mediaName = String(update?.media_name || "");
  const mediaUrl = String(update?.media_url || "");
  return IMAGE_ATTACHMENT_REGEX.test(`${mediaName} ${mediaUrl}`);
}

const UPDATE_TAG_LABELS = { new: "Feature", improved: "Improvement" };

function getUpdateTagLabel(tag) {
  return UPDATE_TAG_LABELS[tag] || getProductUpdateTagLabel(tag);
}

// Page numbers around the current page, with "ellipsis" for the gaps.
function buildPageList(current, last) {
  if (last <= 7) {
    return Array.from({ length: last }, (_, index) => index + 1);
  }

  const start = Math.max(2, Math.min(current - 1, last - 4));
  const end = Math.min(last - 1, Math.max(current + 1, 5));
  const pages = [1];

  if (start > 2) pages.push("ellipsis");
  for (let value = start; value <= end; value += 1) pages.push(value);
  if (end < last - 1) pages.push("ellipsis");
  pages.push(last);

  return pages;
}

const PER_PAGE = 6;

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Text with every occurrence of the search term picked out.
function Highlighted({ text, term }) {
  if (!term) return text;

  return String(text)
    .split(new RegExp(`(${escapeRegExp(term)})`, "ig"))
    .map((part, index) =>
      index % 2 === 1 ? (
        <span key={index} className="text-fe-primary font-medium">
          {part}
        </span>
      ) : (
        part
      ),
    );
}

// A short piece of the description around the first match.
function buildSnippet(text, term) {
  const source = String(text || "");
  const matchIndex = source.toLowerCase().indexOf(term.toLowerCase());
  const start = matchIndex > 30 ? matchIndex - 30 : 0;
  const snippet = source.slice(start, start + 110).trim();

  return `${start > 0 ? "... " : ""}${snippet}${start + 110 < source.length ? "..." : ""}`;
}

export default function ProductUpdatesPublicPage() {
  const api = useApi();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tagFilter, setTagFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [tagCounts, setTagCounts] = useState({});
  const [previewImage, setPreviewImage] = useState(null);

  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  // False until the page number has been read from the URL.
  const [isReady, setIsReady] = useState(false);

  const searchBoxRef = useRef(null);
  // The update to scroll to once its page has loaded (from a #hash or a
  // search result).
  const pendingAnchorRef = useRef("");

  const debouncedSearch = useDebounce(search.trim(), 300);

  const fetchUpdates = useCallback(
    async (targetPage = 1) => {
      setLoading(true);

      try {
        const params = new URLSearchParams();
        params.set("page", String(targetPage));
        params.set("per_page", String(PER_PAGE));

        if (tagFilter !== "all") {
          params.set("tag", tagFilter);
        }

        const { data, error } = await api.get(
          `/api/product-updates?${params.toString()}`,
        );
        if (error) return;

        const payload = data?.data ?? null;

        setPagination(payload);
        setTagCounts(payload?.tag_counts ?? {});
        setRows(payload?.data ?? []);
      } finally {
        setLoading(false);
      }
    },
    [api, tagFilter],
  );

  // The page and the update to show come from the URL: /updates?page=2#slug
  useEffect(() => {
    const readLocation = () => {
      const params = new URLSearchParams(window.location.search);
      pendingAnchorRef.current = decodeURIComponent(
        window.location.hash.slice(1),
      );
      setPage(Math.max(1, Number(params.get("page")) || 1));
      setIsReady(true);
    };

    readLocation();
    window.addEventListener("popstate", readLocation);
    return () => window.removeEventListener("popstate", readLocation);
  }, []);

  useEffect(() => {
    if (!isReady) return;
    fetchUpdates(page);
  }, [isReady, page, fetchUpdates]);

  // Once the page has loaded, bring the requested update into view.
  useEffect(() => {
    if (loading || !pendingAnchorRef.current) return;

    const target = document.getElementById(pendingAnchorRef.current);
    pendingAnchorRef.current = "";
    if (!target) return;

    const scrollToTarget = (behavior) =>
      target.scrollIntoView({ behavior, block: "start" });
    scrollToTarget("smooth");

    // Screenshots above the update load late and push it down, so keep it in
    // place until they settle or the visitor scrolls.
    const images = Array.from(document.querySelectorAll("article img"));
    const onImageLoad = () => scrollToTarget("auto");
    const stop = () => {
      images.forEach((image) =>
        image.removeEventListener("load", onImageLoad),
      );
      window.removeEventListener("wheel", stop);
      window.removeEventListener("touchmove", stop);
    };

    images.forEach((image) => image.addEventListener("load", onImageLoad));
    window.addEventListener("wheel", stop, { passive: true });
    window.addEventListener("touchmove", stop, { passive: true });
    const timer = window.setTimeout(stop, 3000);

    return () => {
      window.clearTimeout(timer);
      stop();
    };
  }, [loading, rows]);

  useEffect(() => {
    if (!debouncedSearch) {
      setSearchResults([]);
      return;
    }

    let isCurrent = true;
    setIsSearching(true);

    const params = new URLSearchParams();
    params.set("search", debouncedSearch);
    params.set("per_page", String(PER_PAGE));

    api
      .get(`/api/product-updates/search?${params.toString()}`)
      .then(({ data, error }) => {
        if (!isCurrent) return;
        setSearchResults(error ? [] : (data?.data ?? []));
      })
      .finally(() => {
        if (isCurrent) setIsSearching(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [api, debouncedSearch]);

  // Close the results when clicking away or pressing Escape.
  useEffect(() => {
    const onPointerDown = (event) => {
      if (!searchBoxRef.current?.contains(event.target)) {
        setIsSearchOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setIsSearchOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  const lastPage = pagination?.last_page || 1;

  const goToPage = (targetPage, anchor = "") => {
    const query = targetPage > 1 ? `?page=${targetPage}` : "";
    window.history.pushState(
      null,
      "",
      `${window.location.pathname}${query}${anchor ? `#${anchor}` : ""}`,
    );

    if (anchor) {
      pendingAnchorRef.current = anchor;
      // Already on that page: nothing reloads, so scroll straight away.
      if (targetPage === page && tagFilter === "all") {
        setRows((currentRows) => [...currentRows]);
      }
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }

    setPage(targetPage);
  };

  const selectTag = (tag) => {
    setTagFilter(tag);
    setPage(1);
    window.history.pushState(null, "", window.location.pathname);
  };

  const openSearchResult = (result) => {
    setIsSearchOpen(false);
    setSearch("");
    // Result pages are counted on the full list, so drop any tag filter.
    setTagFilter("all");
    goToPage(result.page || 1, result.slug);
  };

  // Tags with no updates are hidden, except the one currently selected.
  const availableTags = useMemo(
    () =>
      Array.from(new Set([...Object.keys(tagCounts), tagFilter])).filter(
        (tag) => tag !== "all" && (tagCounts[tag] > 0 || tag === tagFilter),
      ),
    [tagCounts, tagFilter],
  );

  return (
    <main>
      <header className="sticky top-0 z-40 border-b border-neutral-200 bg-white">
        <div className="flex h-[70px] items-center justify-between gap-4 px-4 md:px-8">
          <div className="flex shrink-0 items-center gap-4">
            <Link href="/" aria-label="Zobliss home">
              <ZoblissLogo className="h-auto w-28 md:w-32" />
            </Link>
            <Link
              href="/updates"
              className="hidden rounded-[7px] bg-neutral-100 px-[15px] py-[7px] text-[17px] leading-6 font-medium text-neutral-900 md:block"
            >
              Product Updates
            </Link>
          </div>

          <div ref={searchBoxRef} className="relative w-full max-w-[450px]">
            <Search className="pointer-events-none absolute top-1/2 left-4 z-1 size-5 -translate-y-1/2 text-neutral-500" />
            <Input
              value={search}
              onChange={(value) => {
                setSearch(value);
                setIsSearchOpen(true);
              }}
              onFocus={() => setIsSearchOpen(true)}
              className="h-12 rounded-[10px] border-transparent bg-neutral-100 pl-12 text-base shadow-none"
              placeholder="Search"
            />

            {isSearchOpen && search.trim() ? (
              <div className="absolute top-full right-0 left-0 mt-2 max-h-[70vh] overflow-y-auto rounded-[10px] border border-neutral-200 bg-white py-2 shadow-[0_10px_40px_rgba(0,0,0,0.12)]">
                {searchResults.length > 0 ? (
                  searchResults.map((result) => (
                    <button
                      key={result.id}
                      type="button"
                      onClick={() => openSearchResult(result)}
                      className="block w-full border-b border-neutral-200 px-5 py-4 text-left last:border-b-0 hover:bg-neutral-100"
                    >
                      <span className="block text-[17px] leading-6 font-medium text-neutral-900">
                        <Highlighted
                          text={result.title}
                          term={debouncedSearch}
                        />
                      </span>
                      <span className="mt-1 line-clamp-2 block text-sm leading-5 text-neutral-600">
                        <Highlighted
                          text={buildSnippet(
                            result.description_text,
                            debouncedSearch,
                          )}
                          term={debouncedSearch}
                        />
                      </span>
                    </button>
                  ))
                ) : (
                  <p className="px-5 py-4 text-sm text-neutral-600">
                    {isSearching || debouncedSearch !== search.trim()
                      ? "Searching..."
                      : "No updates found"}
                  </p>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </header>
      <section className="bg-[#efecea] px-4 pt-10 pb-16 md:px-6 md:pt-16 md:pb-24">
        <div className="mx-auto max-w-[720px]">
          <h1 className="text-4xl leading-[1.05] font-medium tracking-tight text-neutral-900 sm:text-[40px]">
            What&apos;s new in Zobliss
          </h1>
          <p className="mt-5 text-lg leading-7 text-neutral-700">
            Stay up to date with the latest Zobliss product updates and new
            features.
          </p>

          <div className="mt-8 flex flex-wrap gap-2">
            {["all", ...availableTags].map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => selectTag(tag)}
                className={cn(
                  "flex h-9 items-center gap-2 rounded-full border px-4 text-sm font-medium transition-colors",
                  tagFilter === tag
                    ? "border-neutral-900 bg-neutral-900 text-white"
                    : "border-neutral-300 bg-white/60 text-neutral-700 hover:bg-white",
                )}
              >
                {tag === "all" ? "All" : getUpdateTagLabel(tag)}
                <span
                  className={cn(
                    "rounded-full px-1.5 text-xs leading-5 tabular-nums",
                    tagFilter === tag
                      ? "bg-white/20 text-white"
                      : "bg-neutral-200 text-neutral-600",
                  )}
                >
                  {tagCounts[tag] ?? 0}
                </span>
              </button>
            ))}
          </div>

          {loading ? (
            <InlineLoader className="h-72" />
          ) : rows.length === 0 ? (
            <div className="mt-10 rounded-[30px] bg-white px-6 py-16 text-center shadow-[0_5px_30px_rgba(0,0,0,0.07)]">
              <p className="text-lg font-medium text-neutral-900">
                No updates found
              </p>
              <p className="mt-2 text-base text-neutral-600">
                Try changing your search or filter.
              </p>
            </div>
          ) : (
            rows.map((update) => (
              <article
                key={update.id}
                id={update.slug || `update-${update.id}`}
                className="mt-10 scroll-mt-28 rounded-[30px] bg-white p-6 shadow-[0_5px_30px_rgba(0,0,0,0.07)] sm:p-[50px] md:mt-[60px]"
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex flex-wrap gap-2">
                    {buildProductUpdateTagList({
                      tags: update.tags,
                      isNew: update.is_new,
                      isImproved: update.is_improved,
                    }).map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full bg-linear-to-r from-[#ffd6c2] via-[#ffe2c4] to-[#fff0c4] px-[18px] py-[7px] text-[13px] leading-4 font-medium tracking-[0.03em] text-neutral-900 uppercase"
                      >
                        {getUpdateTagLabel(tag)}
                      </span>
                    ))}
                  </div>
                  <time
                    dateTime={update.publish_date_date}
                    className="shrink-0 text-[15px] font-medium text-neutral-900"
                  >
                    {formatProductUpdateDate(update.publish_date)}
                  </time>
                </div>

                <h2 className="mt-[30px] text-[26px] leading-[1.15] font-medium tracking-tight text-neutral-900 sm:text-[32px]">
                  {update.title}
                </h2>

                {update.media_url &&
                  (isImageAttachment(update) ? (
                    <button
                      type="button"
                      className="mt-[35px] block w-full cursor-zoom-in overflow-hidden rounded-2xl border border-neutral-200 bg-neutral-50"
                      onClick={() =>
                        setPreviewImage({
                          url: update.media_url,
                          title:
                            update.media_name || update.title || "Attachment",
                        })
                      }
                    >
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src={update.media_url}
                        alt={
                          update.media_name ||
                          update.title ||
                          "Product update attachment"
                        }
                        className="h-auto w-full object-contain"
                        loading="lazy"
                      />
                    </button>
                  ) : null)}

                <ProductUpdateDescription
                  html={update.description_html || update.description}
                  onImageClick={setPreviewImage}
                  className="mt-[35px] text-lg leading-7 wrap-break-word text-neutral-800 [&_a]:text-fe-primary [&_a]:break-all [&_h2]:mt-10 [&_h2]:mb-[18px] [&_h2]:text-[26px] [&_h2]:leading-tight [&_h2]:font-medium [&_h2]:text-neutral-900 [&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:text-xl [&_h3]:font-medium [&_h3]:text-neutral-900 [&_img]:my-6 [&_img]:w-full [&_img]:max-w-full [&_img]:rounded-2xl [&_img]:border [&_img]:border-neutral-200 [&_li]:mb-3 [&_li]:pl-1 [&_ol]:mb-[18px] [&_ol]:pl-9 [&_p]:mb-[18px] [&_strong]:font-semibold [&_strong]:text-neutral-900 [&_ul]:mb-[18px] [&_ul]:pl-9 [&>*:last-child]:mb-0"
                />

                {update.media_url && !isImageAttachment(update) && (
                  <a
                    href={update.media_url}
                    target="_blank"
                    rel="noreferrer"
                    className="bg-fe-primary mt-[35px] inline-flex h-12 items-center rounded-full px-[30px] text-[15px] font-medium text-white transition-opacity hover:opacity-90"
                  >
                    Open attachment
                  </a>
                )}
              </article>
            ))
          )}

          {!loading && lastPage > 1 && (
            <nav
              aria-label="Pagination"
              className="mt-[60px] flex items-center justify-center gap-2.5 border-t border-neutral-300 pt-10"
            >
              <button
                type="button"
                aria-label="Previous page"
                disabled={page <= 1}
                onClick={() => goToPage(page - 1)}
                className="text-fe-primary flex size-[30px] items-center justify-center disabled:text-neutral-400"
              >
                <ChevronLeft className="size-4" />
              </button>
              {buildPageList(page, lastPage).map((item, index) =>
                item === "ellipsis" ? (
                  <span
                    key={`ellipsis-${index}`}
                    className="px-0.5 text-neutral-900"
                  >
                    •••
                  </span>
                ) : (
                  <button
                    key={item}
                    type="button"
                    aria-current={item === page ? "page" : undefined}
                    onClick={() => goToPage(item)}
                    className={cn(
                      "h-[30px] min-w-[30px] rounded-[10px] border border-neutral-300 px-1.5 text-[15px] text-neutral-900 transition-colors",
                      item === page
                        ? "bg-neutral-200"
                        : "bg-white hover:bg-neutral-100",
                    )}
                  >
                    {item}
                  </button>
                ),
              )}
              <button
                type="button"
                aria-label="Next page"
                disabled={page >= lastPage}
                onClick={() => goToPage(page + 1)}
                className="text-fe-primary flex size-[30px] items-center justify-center disabled:text-neutral-400"
              >
                <ChevronRight className="size-4" />
              </button>
            </nav>
          )}

          <div className="mt-[60px] flex flex-col items-start gap-6 rounded-[30px] bg-white p-8 shadow-[0_5px_30px_rgba(0,0,0,0.07)] sm:flex-row sm:items-center sm:p-[50px]">
            <MessageCircleQuestion
              className="size-12 shrink-0 text-neutral-900"
              strokeWidth={1.5}
            />
            <div>
              <h2 className="text-2xl leading-tight font-medium tracking-tight text-neutral-900">
                Can&apos;t find what you&apos;re looking for?
              </h2>
              <p className="mt-2 text-base leading-7 text-neutral-800">
                Browse the{" "}
                <Link href="/help-center" className="font-medium underline">
                  help center
                </Link>{" "}
                or{" "}
                <Link href="/contact" className="font-medium underline">
                  contact us
                </Link>{" "}
                to talk to a real person and get your questions answered.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Dialog
        open={Boolean(previewImage?.url)}
        onOpenChange={(open) => {
          if (!open) {
            setPreviewImage(null);
          }
        }}
      >
        <DialogContent className="max-w-6xl gap-0 border-neutral-200 p-0">
          <DialogHeader className="p-0">
            <DialogTitle></DialogTitle>
            <DialogDescription className="sr-only"></DialogDescription>
          </DialogHeader>
          <div className="rounded-md">
            {previewImage?.url && (
              <>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={previewImage.url}
                  alt={previewImage.title || "Attachment preview"}
                  className="mx-auto h-auto max-h-[82vh] w-auto max-w-full object-contain"
                />
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <Footer />
    </main>
  );
}
